<template>
  <div class="wrapper fair-app">
    <RouterView />

    <!-- FE-024 — Figma `Bottomnav bar` 11:39225: the one mobile bottom bar, every route below 1024.
         The legacy PrimeVue Dock (`features/bottom-app-bar`) is gone. -->
    <FairBottomNavController v-if="showFairBottomNav" />

    <!-- FE-034 — cookie consent banner (Figma `Cookies` 39:41637), until a choice is stored. -->
    <FairCookieBanner v-if="isClient" />
    <!-- Decision 18 — updated terms for a signed-in shopper (after the cookie banner). -->
    <FairConsentsBanner v-if="isClient" />
    <!-- Decisions 15 / 16 / 19 — revoked session, scheduled deletion, sign-in transfer. -->
    <FairAccountSessionHost v-if="isClient" />

    <!-- Quizzes (Figma Quizzes) — mounted on the first open: their queries are not needed before. -->
    <StyleQuiz v-if="styleQuizStore.session > 0" />
    <SizesQuiz v-if="sizesQuizStore.session > 0" />
    <LifestyleQuiz v-if="lifestyleQuizStore.session > 0" />
    <GiftQuiz v-if="giftQuizStore.session > 0" />
    <OutfitQuiz v-if="outfitQuizStore.session > 0" />

    <AuthWrapper />

    <AiraFloatingWidget v-if="showAiraWidget" />
    <AiraContextualCard v-if="showAiraWidget" />
    <AiraChatDrawer v-if="showAiraWidget" />
    <AiraDevPanel v-if="showAiraDevPanel" />
    <AiraBlackoutOverlay v-if="isClient" />

    <FairPageLoader />
    <FairToastHost />
  </div>
</template>

<script setup lang="ts">
import { RouterView, useRoute } from "vue-router";
import { computed, onMounted, ref, watch } from "vue";
import { AiraFloatingWidget, AiraDevPanel, AiraBlackoutOverlay } from "@/features/aira-widget";
import { AiraContextualCard } from "@/features/aira/contextual";
import AiraChatDrawer from "@/widgets/aira/AiraChatDrawer.vue";
import AuthWrapper from "@/features/auth/ui/modals/AuthWrapper.vue";
import StyleQuiz from "@/features/style-quiz/ui/StyleQuiz.vue";
import { useStyleQuizStore } from "@/features/style-quiz/store";
import SizesQuiz from "@/features/sizes-quiz/ui/SizesQuiz.vue";
import { useSizesQuizStore } from "@/features/sizes-quiz/store";
import LifestyleQuiz from "@/features/lifestyle-quiz/ui/LifestyleQuiz.vue";
import { useLifestyleQuizStore } from "@/features/lifestyle-quiz/store";
import GiftQuiz from "@/features/gift-quiz/ui/GiftQuiz.vue";
import { useGiftQuizStore } from "@/features/gift-quiz/store";
import OutfitQuiz from "@/features/outfit-quiz/ui/OutfitQuiz.vue";
import { useOutfitQuizStore } from "@/features/outfit-quiz/store";
import { FairBottomNavController } from "@/features/bottom-nav";
import { FairCookieBanner } from "@/features/cookie-consent";
import FairAccountSessionHost from "@/features/account/ui/FairAccountSessionHost.vue";
import FairConsentsBanner from "@/features/account/ui/FairConsentsBanner.vue";
import { useDevice } from "@/composables/useDevice";
import { useTokenStore } from "@/features/auth/store";
import { useAccountLocaleSync } from "@/features/survey/model/useAccountLocale";
import { useUserProfileDataQuery } from "@/queries/profile/useUserProfileDataQuery";
import { FairPageLoader } from "@/ui/patterns/FairPageLoader";
import { FairToastHost } from "@/ui/patterns/FairToast";

const { isDevice } = useDevice();
const route = useRoute();
const deviceType = computed(() => isDevice.value);

/** Rive needs a real canvas — render the widget after client mount, never during SSG. */
const isClient = ref(false);
onMounted(() => {
  isClient.value = true;
});

/** The ball is hidden where AIRA already has a surface: the chat page and the playground canvas. */
const showAiraWidget = computed(
  () =>
    isClient.value &&
    route.name !== "chatbot" &&
    route.name !== "style-space-playground",
);

/** FE-024 — the mobile bottom nav bar: every route below 1024 except the AIRA chat itself. */
const showFairBottomNav = computed(() => {
  if (!isClient.value) return false;
  const isCompactDevice =
    deviceType.value === "mobile" || deviceType.value === "tablet";
  return isCompactDevice && route.name !== "chatbot";
});

/** Manual state toggles for Aira, dev builds only, opened with ?aira=debug. */
const showAiraDevPanel = computed(
  () => import.meta.env.DEV && showAiraWidget.value && route.query.aira === "debug",
);

// Security: close all modals when user is logged out (token cleared)
const tokenStore = useTokenStore();
const styleQuizStore = useStyleQuizStore();
const sizesQuizStore = useSizesQuizStore();
const lifestyleQuizStore = useLifestyleQuizStore();
const giftQuizStore = useGiftQuizStore();
const outfitQuizStore = useOutfitQuizStore();
// BE-201 — a signed-in account's language / currency win over the browser's guest values.
const accountProfileQuery = useUserProfileDataQuery(computed(() => Boolean(tokenStore.accessToken)));
useAccountLocaleSync(computed(() => (tokenStore.accessToken ? accountProfileQuery.data.value?.user ?? null : null)));
watch(() => tokenStore.accessToken, (newToken, oldToken) => {
  if (oldToken && !newToken) {
    // Token was cleared — user logged out or session expired
    styleQuizStore.close();
    sizesQuizStore.close();
    lifestyleQuizStore.close();
    giftQuizStore.close();
    outfitQuizStore.close();
  }
});
</script>

<style scoped>
.wrapper {
  display: flex;
  justify-content: center;
}
</style>
